import type { CSSProperties, JSX } from 'react'

import { ChannelIcon, Heading, VisuallyHidden } from '@buffer-mono/popcorn'

import { getChannelColor } from '~publish/helpers/channels'
import {
  GUEST_NETWORKS,
  getGuestNetworkService,
} from '~publish/helpers/guestNetworks'
import type {
  GuestNetwork,
  GuestNetworkId,
} from '~publish/helpers/guestNetworks'

import styles from './GuestDraftNetworks.module.css'

interface NetworkChipStyle extends CSSProperties {
  '--channel-color': string
}

function getSelectedNetworks(
  networks: readonly GuestNetworkId[] | undefined,
): GuestNetwork[] {
  return GUEST_NETWORKS.filter((network) => networks?.includes(network.id))
}

export function getGuestDraftNetworkNames(
  networks: readonly GuestNetworkId[] | undefined,
): string[] {
  return getSelectedNetworks(networks).map((network) => network.name)
}

function GuestNetworkChip({
  network,
  size,
}: {
  network: GuestNetwork
  size: 'small' | 'large'
}): JSX.Element {
  const service = getGuestNetworkService(network)
  const style: NetworkChipStyle = {
    '--channel-color': getChannelColor(service),
  }
  return (
    <span
      aria-hidden
      className={size === 'large' ? styles.largeNetwork : styles.network}
      style={style}
    >
      <ChannelIcon type={service} size={size === 'large' ? 16 : 10} />
    </span>
  )
}

export function GuestDraftNetworkIdentity({
  networks,
}: {
  networks: readonly GuestNetworkId[] | undefined
}): JSX.Element | null {
  const selected = getSelectedNetworks(networks)
  const [first] = selected
  if (!first) return null

  return (
    <header className={styles.identity}>
      <span className={styles.networks}>
        {selected.map((network) => (
          <GuestNetworkChip key={network.id} network={network} size="large" />
        ))}
      </span>
      {selected.length === 1 ? (
        <Heading as="p" size="xs" truncate translate="no">
          {first.name}
        </Heading>
      ) : (
        <VisuallyHidden>
          {selected.map((network) => network.name).join(', ')}
        </VisuallyHidden>
      )}
    </header>
  )
}

export function GuestDraftNetworks({
  networks,
  max,
}: {
  networks: readonly GuestNetworkId[] | undefined
  max?: number
}): JSX.Element | null {
  const selected = getSelectedNetworks(networks)
  if (selected.length === 0) return null

  const visible =
    max === undefined || selected.length <= max
      ? selected
      : selected.slice(0, max - 1)
  const hiddenCount = selected.length - visible.length

  return (
    <span className={styles.networks}>
      <VisuallyHidden>
        {selected.map((network) => network.name).join(', ')}
      </VisuallyHidden>
      {visible.map((network) => (
        <GuestNetworkChip key={network.id} network={network} size="small" />
      ))}
      {hiddenCount > 0 ? (
        <span aria-hidden className={styles.more}>
          +{hiddenCount}
        </span>
      ) : null}
    </span>
  )
}
